import { TaskStateRef, TaskKindRef, UserRef } from '@crew/models/refs'
import { useTranslation } from '@crew/modules/i18n'
import { getDefaultTabValue } from '@crew/utils/enum'
import BaselineDelete from '~icons/ic/baseline-delete'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewBadge } from 'components/elements/crewBadge/crewBadge'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { CrewTaskPriority } from 'components/elements/crewTaskPriority/crewTaskPriority'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { TaskDetailListTabs, TaskDependencyType } from 'enums/app'
import { FC, memo, useCallback } from 'react'

export type DependencyTask = {
  id: string
  subject: string
  dueDate: string | null
  assignToUser: UserRef | null
  taskPriority: number
  taskKind: TaskKindRef
  taskState: TaskStateRef
  version: number
}

type TaskDetailDependencyItemProps =
  | {
      dependencyType: typeof TaskDependencyType.ParentChildRelation
      task: DependencyTask
      onDelete: (task: DependencyTask) => void
    }
  | {
      // sf / fs などの時系列依存関係の場合は、依存関係IDとバージョンも渡す
      dependencyType: typeof TaskDependencyType.TemporalRelation
      task: DependencyTask & {
        dependencyId: string
        dependencyVersion: number
      }
      onDelete: (id: string, version: number) => void
    }

// タスク依存関係アイテムコンポーネント
export const TaskDetailDependencyItem: FC<TaskDetailDependencyItemProps> = memo(
  (props) => {
    const { t } = useTranslation()

    const handleDeleteButtonClick = useCallback(() => {
      if (props.dependencyType === TaskDependencyType.ParentChildRelation) {
        props.onDelete(props.task)
        return
      } else if (props.dependencyType === TaskDependencyType.TemporalRelation) {
        props.onDelete(props.task.dependencyId, props.task.dependencyVersion)
      }
    }, [props])

    return (
      // gridで要素を配置し、対象の要素がない場合でもスペースを確保する
      <div className="grid grid-cols-[auto_2fr_80px_80px_0.8fr_auto_40px] gap-2 items-center">
        {/* タスク種別バッジ */}
        <CrewBadge displayColor={props.task.taskKind.displayColor}>
          {props.task.taskKind.name}
        </CrewBadge>

        {/* Subject */}
        <CrewLink
          to={`/tasks/${props.task.id}/${getDefaultTabValue(
            TaskDetailListTabs
          )}`}
          title={props.task.subject}
          className="line-clamp-2 break-all"
        >
          {props.task.subject}
        </CrewLink>

        {/* Due Date */}
        <p>
          {t('format.date', {
            value: props.task.dueDate,
          })}
        </p>

        {/* Priority */}
        <CrewTaskPriority taskPriority={props.task.taskPriority} />

        {/* Assign to user */}
        {/* gridでの配置崩れ防止のため、divは条件付きレンダリングの外側に出しておく */}
        <div className="min-w-0">
          {props.task.assignToUser && (
            <CrewUserAvatar
              userId={props.task.assignToUser.id}
              displayName={props.task.assignToUser.displayName}
              size={CrewAvatarSize.xs}
              showLabel={true}
              cacheValue={
                props.task.assignToUser.id + props.task.assignToUser.version
              }
            />
          )}
        </div>

        {/* Task state */}
        <CrewBadge displayColor={props.task.taskState.displayColor}>
          {props.task.taskState.name}
        </CrewBadge>

        {/* action delete */}
        <CrewButton
          stylingMode="text"
          onClick={handleDeleteButtonClick}
          icon={<BaselineDelete width={20} height={20} />}
          size="sm"
        />
      </div>
    )
  }
)
